import { Box, Divider, Typography } from "@mui/material";
import React, { useState, useRef } from "react";
import LOGO from "assets/images/logo.png";
import Table from "common/Table";
import { useQuery } from "@apollo/client";
import Toaster from "common/Toaster";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { CURRENCY } from "utils/constants";
import ReactToPrint from "react-to-print";
import CButton from "common/CButton";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { GET_SINGLE_RECEIPT_INVOICE } from "components/invoiceTemplate/graphql/query";
import InvoiceHeader from "../InvoiceHeader";
import ListComponent from "../ListComponent";
import UserListComponent from "../UserListComponent";
import InvoiceFooter from "../InvoiceFooter";
import TermsAndCondition from "../TermsAndCondition";

const responseName = {
  personalTrainingPackageBookings: "personalTrainingPackage",
  membershipPackageBookings: "membershipPackage",
  nutritionPlanPackageBookings: "dietPlanPackage",
};

const InvoiceReceipt = () => {
  const { invoiceId } = useParams();
  const componentRef = useRef(null);
  const { pathname } = useLocation();
  const responseURL = pathname.split("/")[2];

  const [packageList, setPackageList] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { data } = useQuery(GET_SINGLE_RECEIPT_INVOICE, {
    skip: invoiceId ? false : true,
    onError: (error) => {
      Toaster({
        type: "error",
        message: error.message,
      });
    },
    onCompleted: (res) => {
      let tempRes = res?.invoiceReceipt?.packages?.edges?.map((item) => {
        return {
          ...item.node,
          invoiceAcutalPrice:
            res?.invoiceReceipt?.invoices?.edges?.length > 0
              ? res?.invoiceReceipt?.invoices?.edges?.[0]?.node?.actualPrice
              : "",
        };
      });

      setPackageList(tempRes);
    },
    variables: { objectId: invoiceId },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const handleDownloadPdf = async (invoiceData) => {
    setDownloadLoading(true);
    const element = componentRef.current;
    const canvas = await html2canvas(element);
    const data = canvas.toDataURL("image/png");

    const pdf = new jsPDF();
    const imgProperties = pdf.getImageProperties(data);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;

    pdf.addImage(data, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(
      invoiceData?.invoiceReceipt?.customer?.user?.name
        ? `${invoiceData?.invoiceReceipt?.customer?.user?.name}_invoice_receipt.pdf`
        : "invoice_receipt.pdf"
    );
    setDownloadLoading(false);
  };

  const columns = [
    {
      field: "title",
      headerName: "Item Description",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Box>{row?.[responseName[responseURL]]?.package?.name ?? "-"}</Box>
        );
      },
    },
    {
      field: "Qty",
      headerName: "Quantity",
      flex: 1,
      valueGetter: () => {
        return 1;
      },
    },
    {
      field: "price",
      headerName: "Amount",
      flex: 0.75,
      renderCell: ({ row }) => {
        return <Box>{row?.invoiceAcutalPrice ?? "-"}</Box>;
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ maxWidth: 800, width: "100%", margin: "auto" }}>
        <Box
          sx={{
            display: "flex",
            height: 40,
            gap: 3,
            justifyContent: "flex-end",
            mr: 5,
          }}
        >
          <ReactToPrint
            trigger={() => {
              return <CButton label="Print Receipt" style={{ width: 150 }} />;
            }}
            content={() => componentRef.current}
          />

          <CButton
            onClick={() => handleDownloadPdf(data)}
            label="Download Receipt"
            style={{ width: 180 }}
            isLoading={downloadLoading}
          />
        </Box>
        <Box sx={{ px: 5, mt: 5 }} ref={componentRef}>
          <Box sx={{ border: 1, borderColor: "rgba(0,0,0,0.2)", p: 5 }}>
            <InvoiceHeader isReceipt data={data?.invoiceReceipt} />

            <Box
              sx={{
                display: "flex",
                mt: 5,
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600 }} color="grey">
                  Bill To
                </Typography>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                >
                  {data?.invoiceReceipt?.customer?.user?.name ?? "-"}
                </Typography>
                <UserListComponent
                  title="Member Id"
                  data={
                    data?.invoiceReceipt?.customer?.user?.memberInfo
                      ?.objectId ?? "-"
                  }
                />
                <UserListComponent
                  title="Contact No"
                  data={data?.invoiceReceipt?.customer?.user?.phone ?? "-"}
                />
                <UserListComponent
                  title="Email"
                  data={data?.invoiceReceipt?.customer?.user?.email ?? "-"}
                />
              </Box>
              <Box sx={{ width: "100%" }}>
                <ListComponent
                  title="Receipt Date"
                  data={
                    moment(data?.invoiceReceipt?.createdOn).format(
                      "DD MMM YYYY hh:mm a"
                    ) ?? "-"
                  }
                />
                <ListComponent
                  title="Payment Type"
                  data={data?.invoiceReceipt?.paymentType ?? "-"}
                />
                <ListComponent
                  title="Start Date"
                  data={
                    packageList.length > 0
                      ? moment(
                          packageList[0]?.[responseName[responseURL]]?.startDate
                        ).format("DD MMM YYYY")
                      : "-"
                  }
                />
                <ListComponent
                  title="End Date"
                  data={
                    packageList.length > 0
                      ? moment(
                          packageList[0]?.[responseName[responseURL]]?.endDate
                        ).format("DD MMM YYYY")
                      : "-"
                  }
                />
                {data?.invoiceReceipt?.paymentType === "card" ? (
                  <>
                    <ListComponent
                      title="Bank"
                      data={data?.invoiceReceipt?.bank?.name ?? "-"}
                    />
                    <ListComponent
                      title="Card No."
                      data={
                        data?.invoiceReceipt?.chequeNo
                          ? data?.invoiceReceipt?.chequeNo
                          : "-"
                      }
                    />
                  </>
                ) : null}
                {data?.invoiceReceipt?.paymentType === "cheque" ? (
                  <>
                    <ListComponent
                      title="Bank"
                      data={data?.invoiceReceipt?.bank?.name ?? "-"}
                    />
                    <ListComponent
                      title="Cheque No."
                      data={
                        data?.invoiceReceipt?.chequeNo
                          ? data?.invoiceReceipt?.chequeNo
                          : "-"
                      }
                    />
                    <ListComponent
                      title="Cheque Date"
                      data={data?.invoiceReceipt?.chequeDate ?? "-"}
                    />
                  </>
                ) : null}
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Table
                columns={columns}
                rows={packageList}
                hidePagination
                isLocalSort
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Box sx={{ width: "50%", mt: 3 }}>
                  <ListComponent
                    title={`Remaining Amount`}
                    data={`${data?.invoiceReceipt?.balance ?? "-"} ${CURRENCY}`}
                  />
                  <Divider sx={{ my: 2 }} />
                  <ListComponent
                    title="Paid Amount"
                    data={
                      `${
                        data?.invoiceReceipt?.paidAmount ?? "-"
                      } ${CURRENCY}` ?? "-"
                    }
                    textStyle={{
                      fontSize: 22,
                      color: "black",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <TermsAndCondition />
            <InvoiceFooter data={data?.invoiceReceipt} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceReceipt;
